import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { GIButton } from 'app-components';
import { RangeCalendar } from '../../../../../../components/common/date-range-picker/RangeCalendar';
import { useAccountActivityState } from '../../../../../../hooks/useAccountActivityState';
import { ConditionalRendering } from '../../../../../../components/common/conditional-rendering';
import {
    dateMethods,
    getCurrentDate,
    getEndOfMonth,
    getStartOfMonth,
    substractDays,
    substractMonths,
} from '../../../../../../utils/dates/dateMethods';
import { LAST_30_DAYS_COUNT, LAST_DAY_COUNT, LAST_MONTH_COUNT, LAST_WEEK_DAYS_COUNT } from '../../accountActivity.constants';

const StyledDropdownContainer = styled.div`
    position: relative;
    display: inline-block;
    width: 100%;
    height: 40px;
`;

const StyledDropdownHeader = styled.div`
    background: white;
    border: ${({ borderSize }) => `${borderSize} solid`};
    border-color: ${({ theme }) => `${theme.gray7}`};
    padding: 8px 0px 8px 12px;
    cursor: pointer;
    display: flex;
    justify-content: left;
    align-items: center;
    height: 40px;
`;

const StyledOverlayContainer = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    background: white;
    ${({ isCalendarOpen }) => isCalendarOpen && 'width: 280%'};
    border: ${({ theme }) => `1px solid ${theme.gray7}`};
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
`;

const StyledOptionWrapper = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    &:hover {
        background: #0088CC;
        color: ${({ theme }) => `${theme.defaultWhite}`};
    }
    margin: 8px;
    background-color: ${({ isSelected, theme }) => `${isSelected ? '#0088CC' : theme.gray10}`};
    color: ${({ isSelected, theme }) => `${isSelected ? theme.defaultWhite : '#0088CC'}`};
    border-radius: 4px;
`;

const StyledOptionsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    text-align: left;
`;

const StyledCalendarContainer = styled.div`
    padding: 16px;
    z-index: 1000;
    flex: 1;
`;

const StyledCalendarFooter = styled.div`
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const StyledDropdownLabel = styled.span`
    flex: 1;
`;

export const DateRangePickerDropdown: React.FC<any> = ({ onDropdownChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string>('all');
    const [dropdownLabel, setDropdownLabel] = useState<string>('All');
    const [isCalendarVisible, setIsCalendarVisible] = useState(false);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const { borderSize } = useAccountActivityState();
    const intlApi = useIntl();
    const [customRange, setCustomRange] = useState({
        startDate: substractDays(new Date(), LAST_30_DAYS_COUNT),
        endDate: getCurrentDate(),
    });

    const dropdownRef = useRef<HTMLDivElement>(null);
    const dateOptionMap = useCallback(
        () => ({
            all: () => setDateRange([null, null]),
            today: () => setDateRange([getCurrentDate(), getCurrentDate()]),
            yesterday: () => setDateRange([substractDays(new Date(), LAST_DAY_COUNT), substractDays(new Date(), LAST_DAY_COUNT)]),
            last7days: () => setDateRange([substractDays(new Date(), LAST_WEEK_DAYS_COUNT), getCurrentDate()]),
            last30days: () => setDateRange([substractDays(new Date(), LAST_30_DAYS_COUNT), getCurrentDate()]),
            thismonth: () => setDateRange([getStartOfMonth(new Date()), getEndOfMonth(new Date())]),
            lastmonth: () =>
                setDateRange([
                    getStartOfMonth(substractMonths(new Date(), LAST_MONTH_COUNT)),
                    getEndOfMonth(substractMonths(new Date(), LAST_MONTH_COUNT)),
                ]),
        }),
        [],
    );

    // Update dropdown label when date range or selected option changes
    useEffect(() => {
        if (selectedOption !== 'custom') {
            const label =
                selectedOption === 'all'
                    ? intlApi.formatMessage({ id: 'daterangepicker.dropdown.option.all' })
                    : `${dateMethods.getDateFormatted(dateRange[0])} - ${dateMethods.getDateFormatted(dateRange[1])}`;
            setDropdownLabel(label);
            onDropdownChange(dateRange);
        }
    }, [selectedOption, dateRange, onDropdownChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Toggle dropdown visibility
    const toggleDropdown = () => setIsOpen(prevState => !prevState);

    // Cancel custom date range selection
    const closeDropdown = () => {
        setIsCalendarVisible(false);
        setIsOpen(false);
    };

    // Handle selecting a date range option
    const handleOptionSelect = (value: string) => {
        setSelectedOption(value);
        setIsCalendarVisible(value === 'custom');

        if (value !== 'custom') {
            const dateMap = dateOptionMap();
            dateMap[value]?.();
            closeDropdown();
        }
    };

    // Apply custom date range
    const handleApply = () => {
        setDateRange([customRange.startDate, customRange.endDate]);
        const label = `${dateMethods.getDateFormatted(customRange.startDate)} - ${dateMethods.getDateFormatted(
            customRange.endDate,
        )}`;
        setDropdownLabel(label);
        onDropdownChange([customRange.startDate, customRange.endDate]);
        setIsCalendarVisible(false);
        setIsOpen(false);
    };

    // Date range options
    const options = [
        { label: 'daterangepicker.dropdown.option.all', value: 'all' },
        { label: 'daterangepicker.dropdown.option.today', value: 'today' },
        { label: 'daterangepicker.dropdown.option.yesterday', value: 'yesterday' },
        { label: 'daterangepicker.dropdown.option.last7days', value: 'last7days' },
        { label: 'daterangepicker.dropdown.option.last30days', value: 'last30days' },
        { label: 'daterangepicker.dropdown.option.thisMonth', value: 'thismonth' },
        { label: 'daterangepicker.dropdown.option.lastMonth', value: 'lastmonth' },
        { label: 'daterangepicker.dropdown.option.custom', value: 'custom' },
    ];

    return (
        <>
            <label className="d-block">
                <FormattedMessage id="daterangepicker.dropdown.label" />
            </label>
            <StyledDropdownContainer ref={dropdownRef}>
                <StyledDropdownHeader data-testid="dropdown" borderSize={borderSize} onClick={toggleDropdown}>
                    <i className="far fa-calendar-alt pr-2"></i>
                    <StyledDropdownLabel>{dropdownLabel}</StyledDropdownLabel>
                    <i className="fas fa-angle-down pr-2"></i>
                </StyledDropdownHeader>
                <ConditionalRendering isTrue={isOpen}>
                    <StyledOverlayContainer isCalendarOpen={isCalendarVisible}>
                        <StyledOptionsContainer>
                            <div>
                                {options.map(option => (
                                    <StyledOptionWrapper
                                        key={option.value}
                                        onClick={() => {
                                            handleOptionSelect(option.value);
                                        }}
                                        isSelected={selectedOption === option.value}
                                    >
                                        <FormattedMessage id={option.label} />
                                    </StyledOptionWrapper>
                                ))}
                            </div>
                            <ConditionalRendering isTrue={isCalendarVisible}>
                                <StyledCalendarContainer id="calendar-container">
                                    <RangeCalendar setCalendarRange={setCustomRange} />
                                    <StyledCalendarFooter>
                                        <GIButton
                                            id="cancel_btn"
                                            labelId="button.cancel"
                                            variant="secondary"
                                            onClickHandler={closeDropdown}
                                        />
                                        <GIButton id="apply_btn" labelId="button.apply" onClickHandler={handleApply} />
                                    </StyledCalendarFooter>
                                </StyledCalendarContainer>
                            </ConditionalRendering>
                        </StyledOptionsContainer>
                    </StyledOverlayContainer>
                </ConditionalRendering>
            </StyledDropdownContainer>
        </>
    );
};
